import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { firestore, functions } from '../../firebase';
import { notificationService } from '../../services/notificationService';

type SpecializationStatus = 'pending' | 'approved' | 'rejected' | 'inactive';

interface SpecializationRecord {
  id: string;
  name: string;
  category: string;
  status: SpecializationStatus;
  createdAt?: Timestamp;
  approvedAt?: Timestamp;
  suggestedBy?: string;
  source?: 'suggested' | 'admin';
}

type AdminView = 'moderation' | 'catalog';
type CatalogStatusFilter = 'all' | 'approved' | 'inactive';

const manageSpecializationStatusFn = httpsCallable<
  { specializationId: string; newStatus: 'approved' | 'rejected' },
  { success: boolean; message?: string }
>(functions, 'manageSpecializationStatus');

const adminCreateSpecializationFn = httpsCallable<
  { name: string; category: string },
  { success: boolean; specializationId: string }
>(functions, 'adminCreateSpecialization');

const adminUpdateSpecializationFn = httpsCallable<
  { specializationId: string; name: string; category: string },
  { success: boolean }
>(functions, 'adminUpdateSpecialization');

const adminSetSpecializationActiveStateFn = httpsCallable<
  { specializationId: string; action: 'deactivate' | 'reactivate' },
  { success: boolean; status: 'approved' | 'inactive' }
>(functions, 'adminSetSpecializationActiveState');

const formatDateTime = (timestamp?: Timestamp): string => {
  if (!timestamp?.toDate) {
    return 'Brak daty';
  }
  return timestamp.toDate().toLocaleString('pl-PL');
};

const normalizeInput = (value: string): string => value.trim().replace(/\s+/g, ' ');

const SpecializationManagementPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestedView, targetSpecializationId, targetNotificationId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = String(params.get('view') || '').trim();
    return {
      requestedView: viewParam === 'catalog' ? 'catalog' : 'moderation',
      targetSpecializationId: String(params.get('specializationId') || '').trim(),
      targetNotificationId: String(params.get('notificationId') || '').trim(),
    } as const;
  }, [location.search]);

  const [view, setView] = useState<AdminView>(requestedView);
  const [pendingSpecializations, setPendingSpecializations] = useState<SpecializationRecord[]>([]);
  const [catalogSpecializations, setCatalogSpecializations] = useState<SpecializationRecord[]>([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [readbackError, setReadbackError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const handledReadbacksRef = useRef<Set<string>>(new Set());
  const targetCardRef = useRef<HTMLElement | null>(null);
  const mountedRef = useRef(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CatalogStatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [addName, setAddName] = useState('');
  const [addCategory, setAddCategory] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setReadbackError(null);
    if (!targetSpecializationId) {
      setView(requestedView);
      return;
    }

    setView('moderation');
    const params = new URLSearchParams(location.search);
    if (String(params.get('view') || '').trim() === 'moderation') {
      return;
    }
    params.set('view', 'moderation');
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, requestedView, targetSpecializationId]);

  useEffect(() => {
    const pendingQuery = query(
      collection(firestore, 'specializations'),
      where('status', '==', 'pending')
    );
    const catalogQuery = query(
      collection(firestore, 'specializations'),
      where('status', 'in', ['approved', 'inactive'])
    );

    const unsubscribePending = onSnapshot(
      pendingQuery,
      (snapshot) => {
        const rows = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }) as SpecializationRecord)
          .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
        setPendingSpecializations(rows);
        setPendingError(null);
        setLoadingPending(false);
      },
      () => {
        setLoadingPending(false);
        setPendingError('Nie udało się odczytać kolejki moderacji specjalizacji.');
      }
    );

    const unsubscribeCatalog = onSnapshot(
      catalogQuery,
      (snapshot) => {
        const rows = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }) as SpecializationRecord)
          .sort((a, b) => a.name.localeCompare(b.name, 'pl-PL'));
        setCatalogSpecializations(rows);
        setLoadingCatalog(false);
      },
      () => {
        setLoadingCatalog(false);
        setError('Nie udało się odczytać katalogu specjalizacji.');
      }
    );

    return () => {
      unsubscribePending();
      unsubscribeCatalog();
    };
  }, []);

  const categoryOptions = useMemo(() => {
    const unique = new Set<string>();
    catalogSpecializations.forEach((item) => {
      if (item.category) {
        unique.add(item.category);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'pl-PL'));
  }, [catalogSpecializations]);

  const filteredCatalog = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('pl-PL');
    return catalogSpecializations.filter((item) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        item.name.toLocaleLowerCase('pl-PL').includes(normalizedSearch) ||
        item.category.toLocaleLowerCase('pl-PL').includes(normalizedSearch);
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [catalogSpecializations, searchTerm, statusFilter, categoryFilter]);

  const activeCount = useMemo(
    () => catalogSpecializations.filter((item) => item.status === 'approved').length,
    [catalogSpecializations]
  );
  const inactiveCount = catalogSpecializations.length - activeCount;

  const resolvedTarget = useMemo(
    () =>
      targetSpecializationId
        ? pendingSpecializations.find((item) => item.id === targetSpecializationId) || null
        : null,
    [pendingSpecializations, targetSpecializationId]
  );

  const targetMissing = Boolean(
    !loadingPending && !pendingError && targetSpecializationId && !resolvedTarget
  );

  useEffect(() => {
    if (
      loadingPending ||
      pendingError ||
      view !== 'moderation' ||
      requestedView !== 'moderation' ||
      !targetSpecializationId ||
      !targetNotificationId ||
      !resolvedTarget
    ) {
      return;
    }

    const readbackKey = `${targetNotificationId}:${targetSpecializationId}`;
    if (handledReadbacksRef.current.has(readbackKey)) {
      return;
    }
    handledReadbacksRef.current.add(readbackKey);

    void notificationService
      .markSpecializationNotificationAsRead({
        notificationId: targetNotificationId,
        specializationId: targetSpecializationId,
      })
      .then((readBack) => {
        if (readBack) {
          return;
        }
        handledReadbacksRef.current.delete(readbackKey);
        if (mountedRef.current) {
          setReadbackError(
            'Nie udało się potwierdzić tego powiadomienia. Pozostaje ono nieprzeczytane.'
          );
        }
      })
      .catch((readError) => {
        handledReadbacksRef.current.delete(readbackKey);
        console.error('Failed to read back specialization notification:', readError);
        if (mountedRef.current) {
          setReadbackError(
            'Nie udało się potwierdzić tego powiadomienia. Pozostaje ono nieprzeczytane.'
          );
        }
      });
  }, [
    loadingPending,
    pendingError,
    requestedView,
    resolvedTarget,
    targetNotificationId,
    targetSpecializationId,
    view,
  ]);

  useEffect(() => {
    if (
      loadingPending ||
      pendingError ||
      view !== 'moderation' ||
      !resolvedTarget ||
      !targetCardRef.current
    ) {
      return;
    }

    targetCardRef.current.focus({ preventScroll: true });
    targetCardRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [loadingPending, pendingError, resolvedTarget, view]);

  const getFriendlyError = (fallback: string, err: unknown): string => {
    const code = String((err as { code?: string })?.code || '');
    if (code.includes('permission-denied')) {
      return 'Brak uprawnień administratora do tej operacji.';
    }
    if (code.includes('already-exists')) {
      return 'Taka specjalizacja już istnieje lub oczekuje na decyzję.';
    }
    if (code.includes('failed-precondition')) {
      return 'Operacja nie mogła zostać wykonana w aktualnym stanie rekordu.';
    }
    return fallback;
  };

  const handleModeration = async (
    specializationId: string,
    newStatus: 'approved' | 'rejected'
  ): Promise<void> => {
    setBusyId(specializationId);
    setError(null);
    try {
      await manageSpecializationStatusFn({ specializationId, newStatus });
    } catch (err) {
      setError(
        getFriendlyError(
          newStatus === 'approved'
            ? 'Nie udało się zatwierdzić sugestii specjalizacji.'
            : 'Nie udało się odrzucić sugestii specjalizacji.',
          err
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateSpecialization = async () => {
    const normalizedName = normalizeInput(addName);
    const normalizedCategory = normalizeInput(addCategory);
    if (!normalizedName || !normalizedCategory) {
      setError('Podaj nazwę i kategorię specjalizacji.');
      return;
    }

    setAdding(true);
    setError(null);
    try {
      await adminCreateSpecializationFn({ name: normalizedName, category: normalizedCategory });
      setAddName('');
      setAddCategory('');
    } catch (err) {
      setError(getFriendlyError('Nie udało się dodać specjalizacji do katalogu.', err));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (record: SpecializationRecord) => {
    setEditingId(record.id);
    setEditName(record.name);
    setEditCategory(record.category);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditCategory('');
    setSavingEdit(false);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const normalizedName = normalizeInput(editName);
    const normalizedCategory = normalizeInput(editCategory);
    if (!normalizedName || !normalizedCategory) {
      setError('Nazwa i kategoria specjalizacji nie mogą być puste.');
      return;
    }

    setSavingEdit(true);
    setError(null);
    try {
      await adminUpdateSpecializationFn({
        specializationId: editingId,
        name: normalizedName,
        category: normalizedCategory,
      });
      cancelEdit();
    } catch (err) {
      setError(getFriendlyError('Nie udało się zapisać zmian specjalizacji.', err));
      setSavingEdit(false);
    }
  };

  const handleSetActiveState = async (
    specializationId: string,
    action: 'deactivate' | 'reactivate'
  ) => {
    setBusyId(specializationId);
    setError(null);
    try {
      await adminSetSpecializationActiveStateFn({ specializationId, action });
    } catch (err) {
      setError(
        getFriendlyError(
          action === 'deactivate'
            ? 'Nie udało się wycofać specjalizacji.'
            : 'Nie udało się przywrócić specjalizacji.',
          err
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-4">
      <header className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 sm:p-6">
        <h1 className="text-xl font-semibold text-primary-light-100 sm:text-2xl">Specjalizacje</h1>
        <p className="mt-1 text-sm text-primary-light-300">
          Globalna lista specjalizacji jest source of truth dla systemu. Moderacja sugestii i
          katalog działają jako osobne obszary.
        </p>
      </header>

      {error && (
        <div className="rounded-xl border border-status-error-500/40 bg-status-error-500/10 p-3 text-sm text-status-error-500">
          {error}
        </div>
      )}

      {pendingError && (
        <div className="rounded-xl border border-status-error-500/40 bg-status-error-500/10 p-3 text-sm text-status-error-500">
          {pendingError}
        </div>
      )}

      {readbackError && (
        <div className="rounded-xl border border-status-warning-500/40 bg-status-warning-500/10 p-3 text-sm text-status-warning-100">
          {readbackError}
        </div>
      )}

      <div className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-3">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <button
            type="button"
            onClick={() => setView('moderation')}
            className={`inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-semibold transition ${
              view === 'moderation'
                ? 'bg-ratuj-300 text-primary-dark-100'
                : 'bg-primary-dark-100 text-primary-light-300 hover:text-primary-light-100'
            }`}
          >
            Moderacja ({pendingSpecializations.length})
          </button>
          <button
            type="button"
            onClick={() => setView('catalog')}
            className={`inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-semibold transition ${
              view === 'catalog'
                ? 'bg-ratuj-300 text-primary-dark-100'
                : 'bg-primary-dark-100 text-primary-light-300 hover:text-primary-light-100'
            }`}
          >
            Wszystkie ({catalogSpecializations.length})
          </button>
        </div>
      </div>

      {view === 'moderation' && (
        <div className="space-y-3">
          {targetMissing && (
            <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-3 text-sm text-primary-light-300">
              Wskazana propozycja nie jest dostępna w kolejce moderacji. Powiadomienie pozostaje
              nieprzeczytane.
            </p>
          )}

          {loadingPending && (
            <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 text-sm text-primary-light-300">
              Ładowanie kolejki moderacji...
            </p>
          )}

          {!loadingPending &&
            !pendingError &&
            !targetMissing &&
            pendingSpecializations.length === 0 && (
              <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 text-sm text-primary-light-300">
                Brak sugestii specjalizacji oczekujących na decyzję.
              </p>
            )}

          {!loadingPending &&
            pendingSpecializations.map((item) => {
              const isBusy = busyId === item.id;
              const isTarget = item.id === targetSpecializationId;
              return (
                <article
                  key={item.id}
                  ref={isTarget ? targetCardRef : undefined}
                  tabIndex={isTarget ? -1 : undefined}
                  aria-current={isTarget ? 'true' : undefined}
                  className={`rounded-2xl border bg-primary-dark-200 p-4 ${
                    isTarget
                      ? 'border-ratuj-300/70 ring-2 ring-ratuj-300/30'
                      : 'border-primary-dark-300/70'
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="break-words text-base font-semibold text-primary-light-100">
                          {item.name}
                        </h2>
                        {isTarget && (
                          <span className="rounded-full bg-status-warning-500/15 px-2 py-0.5 text-xs font-medium text-status-warning-100">
                            Oczekuje na decyzję
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-primary-light-300">{item.category}</p>
                      <p className="mt-1 text-xs text-primary-light-500">
                        Zgłoszono: {formatDateTime(item.createdAt)}
                      </p>
                      <p className="text-xs text-primary-light-500">
                        Autor: {item.suggestedBy || 'Brak danych'}
                      </p>
                    </div>
                    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                      <button
                        type="button"
                        onClick={() => handleModeration(item.id, 'approved')}
                        disabled={isBusy}
                        className="inline-flex h-10 items-center justify-center gap-1 rounded-lg bg-status-success-500 px-4 text-sm font-semibold text-white transition hover:bg-status-success-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                        Zatwierdź
                      </button>
                      <button
                        type="button"
                        onClick={() => handleModeration(item.id, 'rejected')}
                        disabled={isBusy}
                        className="inline-flex h-10 items-center justify-center gap-1 rounded-lg bg-status-error-500 px-4 text-sm font-semibold text-white transition hover:bg-status-error-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <XCircleIcon className="h-4 w-4" />
                        Odrzuć
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
        </div>
      )}

      {view === 'catalog' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <article className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-primary-light-400">Aktywne</p>
              <p className="mt-2 text-2xl font-semibold text-primary-light-100">{activeCount}</p>
            </article>
            <article className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-primary-light-400">Wycofane</p>
              <p className="mt-2 text-2xl font-semibold text-primary-light-100">{inactiveCount}</p>
            </article>
            <article className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-primary-light-400">Łącznie</p>
              <p className="mt-2 text-2xl font-semibold text-primary-light-100">
                {catalogSpecializations.length}
              </p>
            </article>
          </div>

          <div className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4">
            <h2 className="text-base font-semibold text-primary-light-100">
              Dodaj specjalizację ręcznie
            </h2>
            <p className="mt-1 text-sm text-primary-light-300">
              Dodawanie admina tworzy od razu aktywną pozycję w globalnym katalogu.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <input
                value={addName}
                onChange={(event) => setAddName(event.target.value)}
                placeholder="Nazwa specjalizacji"
                className="h-10 rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100 placeholder:text-primary-light-500"
                disabled={adding}
              />
              <input
                value={addCategory}
                onChange={(event) => setAddCategory(event.target.value)}
                placeholder="Kategoria"
                className="h-10 rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100 placeholder:text-primary-light-500"
                disabled={adding}
              />
              <button
                type="button"
                onClick={handleCreateSpecialization}
                disabled={adding}
                className="inline-flex h-10 items-center justify-center gap-1 rounded-lg bg-ratuj-300 px-4 text-sm font-semibold text-primary-dark-100 transition hover:bg-ratuj-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4" />
                Dodaj
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_180px_220px]">
              <label className="relative block">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-primary-light-500">
                  <MagnifyingGlassIcon className="h-4 w-4" />
                </span>
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Szukaj po nazwie lub kategorii"
                  className="h-10 w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 pl-9 pr-3 text-sm text-primary-light-100 placeholder:text-primary-light-500"
                />
              </label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as CatalogStatusFilter)}
                className="h-10 rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
              >
                <option value="all">Status: wszystkie</option>
                <option value="approved">Status: aktywne</option>
                <option value="inactive">Status: wycofane</option>
              </select>
              <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                className="h-10 rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
              >
                <option value="all">Kategoria: wszystkie</option>
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loadingCatalog && (
            <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 text-sm text-primary-light-300">
              Ładowanie katalogu specjalizacji...
            </p>
          )}

          {!loadingCatalog && filteredCatalog.length === 0 && (
            <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 text-sm text-primary-light-300">
              Brak specjalizacji spełniających aktualne filtry.
            </p>
          )}

          {!loadingCatalog &&
            filteredCatalog.map((item) => {
              const isEditing = editingId === item.id;
              const isBusy = busyId === item.id;
              const isInactive = item.status === 'inactive';
              return (
                <article
                  key={item.id}
                  className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <input
                            value={editName}
                            onChange={(event) => setEditName(event.target.value)}
                            className="h-10 rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                            disabled={savingEdit}
                          />
                          <input
                            value={editCategory}
                            onChange={(event) => setEditCategory(event.target.value)}
                            className="h-10 rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                            disabled={savingEdit}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-semibold text-primary-light-100">
                              {item.name}
                            </h3>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                isInactive
                                  ? 'bg-primary-light-500/20 text-primary-light-300'
                                  : 'bg-status-success-500/20 text-status-success-500'
                              }`}
                            >
                              {isInactive ? 'Wycofana' : 'Aktywna'}
                            </span>
                            {item.source === 'admin' && (
                              <span className="rounded-full bg-ratuj-300/20 px-2 py-0.5 text-xs font-medium text-ratuj-200">
                                Dodana przez admina
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-primary-light-300">{item.category}</p>
                          <p className="mt-1 text-xs text-primary-light-500">
                            Utworzono: {formatDateTime(item.createdAt)} | Zatwierdzono:{' '}
                            {formatDateTime(item.approvedAt)}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            disabled={savingEdit}
                            className="inline-flex h-10 items-center justify-center rounded-lg bg-ratuj-300 px-4 text-sm font-semibold text-primary-dark-100 transition hover:bg-ratuj-400 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Zapisz
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                            className="inline-flex h-10 items-center justify-center rounded-lg border border-primary-dark-300 px-4 text-sm font-semibold text-primary-light-300 transition hover:text-primary-light-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Anuluj
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            disabled={isBusy}
                            className="inline-flex h-10 items-center justify-center gap-1 rounded-lg border border-primary-dark-300 px-4 text-sm font-semibold text-primary-light-300 transition hover:text-primary-light-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edytuj
                          </button>
                          {isInactive ? (
                            <button
                              type="button"
                              onClick={() => handleSetActiveState(item.id, 'reactivate')}
                              disabled={isBusy}
                              className="inline-flex h-10 items-center justify-center gap-1 rounded-lg bg-status-success-500 px-4 text-sm font-semibold text-white transition hover:bg-status-success-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                              Przywróć
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSetActiveState(item.id, 'deactivate')}
                              disabled={isBusy}
                              className="inline-flex h-10 items-center justify-center gap-1 rounded-lg bg-primary-light-500/20 px-4 text-sm font-semibold text-primary-light-200 transition hover:bg-primary-light-500/30 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <NoSymbolIcon className="h-4 w-4" />
                              Wycofaj
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}

          <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-3 text-xs text-primary-light-500">
            Domyślna ścieżka operacyjna to wycofanie i przywracanie specjalizacji. Hard delete nie
            jest używany w głównym flow.
          </p>
        </div>
      )}
    </section>
  );
};

export default SpecializationManagementPage;
